import * as React from 'react';
import strings from '../../../strings/strings.json';
import { FlexCol, FlexColRg, FlexSpaceBetween, Headline1 } from '../../../components/Elements';
import styled from 'styled-components';
import { theme, sizes, breakpoints } from 'theme';
import { StepButtonComponent } from './StepButtonComponent';
import { TabDefinition } from 'components/tabs/Tabs';
import useWindowWidth from 'hooks/useWindowWidth';
import TabStructureComponent from 'components/tabs/TabStructureComponent';
import { CompletionIndicator } from 'components/progressBar/CompletionIndicator';
import { StepId } from 'contextProviders/BuildCourseContext';
import { courseBuilderBreakpoint } from '../shared-styles/courseBuilderFormComponents';

export type Step = {
    id: StepId;
    title: string;
    aria: string;
    icon: React.ElementType;
    content: JSX.Element;
    action: () => void;
    completed: boolean;
};

type StepsPanelProps = {
    courseId: string;
    steps: Step[];
    activeStep: string;
};

export function StepsPanel({ steps, activeStep }: StepsPanelProps): JSX.Element {
    const { currentWindowWidth } = useWindowWidth();

    const tabs: TabDefinition[] = steps.map((step) => {
        return {
            id: step.id,
            name: step.title,
            icon: step.icon,
            content: step.content,
        };
    });

    const stepComponents: JSX.Element[] = steps.map((step) => (
        <StepButtonComponent
            key={step.id}
            title={step.title}
            aria={step.aria}
            icon={step.icon}
            onClick={step.action}
            active={step.id === activeStep}
            completed={step.completed}
        />
    ));

    return (
        <StepsContainer id="create-course-steps">
            <HeadlineAndProgress>
                <Headline1>{strings.courseEditorPage.steps.headline}</Headline1>
                <CompletionIndicator completed={steps.filter((step) => step.completed).length} total={steps.length} />
            </HeadlineAndProgress>
            {currentWindowWidth <= courseBuilderBreakpoint ? (
                <TabStructureComponent
                    tabs={tabs}
                    initialActiveTabId={activeStep}
                    breakpoint={courseBuilderBreakpoint}
                />
            ) : (
                <StepButtons>{stepComponents}</StepButtons>
            )}
        </StepsContainer>
    );
}

const HeadlineAndProgress = styled(FlexSpaceBetween)`
    margin-right: ${sizes.spacingSm};
`;

const StepButtons = styled(FlexCol)`
    border-bottom: 1px solid ${theme.cardSecondaryBorder};
`;

const StepsContainer = styled(FlexColRg)`
    margin-top: ${sizes.spacingLg};

    @media (max-width: ${breakpoints.sm}) {
        margin-top: ${sizes.spacingSm};
    }
`;
